import type { Rect } from './layout'

/**
 * A picture lifted off a mood board's grid (Huanrui, 2026-10-23)
 * -------------------------------------------------------------
 * Lifted, a picture leaves the grid (which lays itself out again without it)
 * and floats over the whole sheet, whole -- not cut -- with an outline in the
 * sheet's ground colour, as wide as the gutter between the pictures. A
 * picture with see-through parts is outlined along its own shape, like a
 * sticker; any other along its edges. It can be moved, sized and turned to
 * any angle.
 *
 * One drawing for every place it shows: the editor draws it on screen with
 * this, and the frame on a project's board and the frame on Miro are given
 * this drawing as a PNG, turned and outlined already, so the three match.
 */
export interface FloatSpec {
  /** Degrees clockwise. */
  angle: number
  /** The outline's width, in the units of w and h (sheet units): the sheet's gutter. 0: none. */
  outline: number
  /** The outline's colour: the sheet's ground. */
  color: string
  /** The picture's own size, as it is (not turned), the outline not included. */
  w: number
  h: number
}

/** The box a lifted picture takes on the sheet, outline and turn included, about its middle. */
export function floatBounds(cx: number, cy: number, spec: FloatSpec): Rect {
  const w = spec.w + 2 * spec.outline
  const h = spec.h + 2 * spec.outline
  const a = (spec.angle * Math.PI) / 180
  const cos = Math.abs(Math.cos(a))
  const sin = Math.abs(Math.sin(a))
  const bw = cos * w + sin * h
  const bh = sin * w + cos * h
  return { x: cx - bw / 2, y: cy - bh / 2, w: bw, h: bh }
}

/**
 * Where a lifted picture may be, its middle moved as little as it takes
 * (Huanrui, 2026-10-23: dragged off the sheet, it was lost): wholly on the
 * sheet, outline and turn included, when it fits there; otherwise its
 * middle on the sheet.
 */
export function keepOnSheet(x: number, y: number, spec: FloatSpec, sheetW: number, sheetH: number): { x: number; y: number } {
  const box = floatBounds(0, 0, spec)
  const keep = (at: number, size: number, sheet: number) =>
    size <= sheet ? Math.min(sheet - size / 2, Math.max(size / 2, at)) : Math.min(sheet, Math.max(0, at))
  return { x: keep(x, box.w, sheetW), y: keep(y, box.h, sheetH) }
}

function canvasOf(width: number, height: number): { canvas: HTMLCanvasElement; pen: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas')
  canvas.width = Math.max(1, Math.ceil(width))
  canvas.height = Math.max(1, Math.ceil(height))
  const pen = canvas.getContext('2d')
  if (!pen) throw new Error('Canvas is unavailable')
  pen.imageSmoothingQuality = 'high'
  return { canvas, pen }
}

/**
 * What a picture's see-through parts amount to (Huanrui, 2026-10-23),
 * looked at on a small copy (128 x 128) in a millisecond or two:
 * - 'cutout': a share of it wholly see-through (alpha 8 or less, at least
 *   half a percent of it) -- a cut-out, kept see-through, outlined along its
 *   shape. A soft shadow or edge round a cut-out stays as it is.
 * - 'semi': no real wholly see-through part, but a fair share (2% or more)
 *   partly see-through -- a picture whose ground was made translucent. Not
 *   meant to show through: laid on white, as it looks on a white page
 *   (flattenAlpha; Huanrui, 2026-10-23), and kept as a JPEG.
 * - 'solid': everything else.
 */
export type AlphaKind = 'solid' | 'cutout' | 'semi'
export function alphaKind(source: CanvasImageSource): AlphaKind {
  try {
    const n = 128
    const { pen } = canvasOf(n, n)
    pen.drawImage(source, 0, 0, n, n)
    const data = pen.getImageData(0, 0, n, n).data
    let clear = 0
    let partly = 0
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] <= 8) clear += 1
      else if (data[i] < 247) partly += 1
    }
    const all = n * n
    if (clear / all >= 0.005) return 'cutout'
    if (partly / all >= 0.02) return 'semi'
    return 'solid'
  } catch {
    // A picture the page may not read (another site's, without leave): treat it as solid.
    return 'solid'
  }
}

/** Has the picture wholly see-through parts (a cut-out)? */
export function hasSeeThrough(source: CanvasImageSource): boolean {
  return alphaKind(source) === 'cutout'
}

/**
 * The picture made solid, laid on white: what it looks like on a white page
 * (Huanrui, 2026-10-23 -- a translucent pink ground comes out the paler pink
 * it shows on white, not its own full pink). At most `longest` pixels on its
 * longer side.
 */
export function flattenAlpha(source: CanvasImageSource & { width: number; height: number }, longest = Infinity): HTMLCanvasElement {
  const k = Math.min(1, longest / Math.max(source.width, source.height))
  const { canvas, pen } = canvasOf(source.width * k, source.height * k)
  pen.fillStyle = '#ffffff'
  pen.fillRect(0, 0, canvas.width, canvas.height)
  pen.drawImage(source, 0, 0, canvas.width, canvas.height)
  return canvas
}

/**
 * A cut picture, as a file, by the rule above (2026-10-23): a cut-out as a
 * see-through PNG; anything else -- a translucent ground included -- laid on
 * white, as it looks on a white page, as a JPEG. One rule for the website
 * and the panel.
 */
export function encodeCut(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  const kind = alphaKind(canvas)
  const pen = canvas.getContext('2d')
  if (!pen) return Promise.reject(new Error('Canvas is unavailable'))
  if (kind !== 'cutout') {
    // White under it: a translucent ground as it looks on white, and a
    // see-through pixel the quick look missed not black.
    pen.globalCompositeOperation = 'destination-over'
    pen.fillStyle = '#ffffff'
    pen.fillRect(0, 0, canvas.width, canvas.height)
    pen.globalCompositeOperation = 'source-over'
  }
  const type = kind === 'cutout' ? 'image/png' : 'image/jpeg'
  return new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((out) => (out ? resolve(out) : reject(new Error('Could not encode the picture'))), type, quality),
  )
}

/**
 * The picture with its outline, not turned: (w + 2 outline) x (h + 2 outline)
 * at `perUnit` pixels a sheet unit. `cutOut` says whether it has see-through
 * parts (hasSeeThrough), so a caller drawing it again and again asks once.
 */
export function drawOutlined(source: CanvasImageSource, spec: FloatSpec, perUnit: number, cutOut: boolean): HTMLCanvasElement {
  const t = Math.max(0, spec.outline * perUnit)
  const pad = Math.ceil(t)
  const pw = Math.max(1, Math.round(spec.w * perUnit))
  const ph = Math.max(1, Math.round(spec.h * perUnit))
  const { canvas, pen } = canvasOf(pw + 2 * pad, ph + 2 * pad)
  if (t >= 0.25) {
    if (!cutOut) {
      // A solid picture: a plain band round its edges, square at the corners.
      pen.fillStyle = spec.color
      pen.fillRect(pad - t, pad - t, pw + 2 * t, ph + 2 * t)
    } else {
      // A cut-out: its shape grown by the outline's width, as the distance
      // from the shape (outlineOf), then the picture itself on top.
      pen.drawImage(source, pad, pad, pw, ph)
      const pixels = pen.getImageData(0, 0, canvas.width, canvas.height)
      outlineOf(pixels, t, spec.color)
      pen.putImageData(pixels, 0, 0)
    }
  }
  pen.drawImage(source, pad, pad, pw, ph)
  return canvas
}

/**
 * The outline of a cut-out, written into its own pixels: every pixel within
 * `t` of the shape (alpha half or more) takes the colour, edged smoothly over
 * the last pixel; the picture is drawn over it afterwards. The distance is
 * worked out exactly for every pixel at once (Felzenszwalb and Huttenlocher's
 * distance transform: a row pass and a column pass), so it costs the same
 * whatever the outline's width -- drawing the picture round a ring of
 * copies, the first way, took 16 seconds for a full-size picture.
 */
function outlineOf(pixels: ImageData, t: number, color: string) {
  const { width: w, height: h, data } = pixels
  const far = 1e20
  const grid = new Float64Array(w * h)
  for (let i = 0; i < w * h; i += 1) grid[i] = data[i * 4 + 3] >= 128 ? 0 : far
  const size = Math.max(w, h)
  const f = new Float64Array(size)
  const d = new Float64Array(size)
  const v = new Int32Array(size)
  const z = new Float64Array(size + 1)
  const pass = (n: number) => {
    let k = 0
    v[0] = 0
    z[0] = -far
    z[1] = far
    for (let q = 1; q < n; q += 1) {
      let s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k])
      while (s <= z[k]) {
        k -= 1
        s = (f[q] + q * q - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k])
      }
      k += 1
      v[k] = q
      z[k] = s
      z[k + 1] = far
    }
    k = 0
    for (let q = 0; q < n; q += 1) {
      while (z[k + 1] < q) k += 1
      const dq = q - v[k]
      d[q] = dq * dq + f[v[k]]
    }
  }
  for (let x = 0; x < w; x += 1) {
    for (let y = 0; y < h; y += 1) f[y] = grid[y * w + x]
    pass(h)
    for (let y = 0; y < h; y += 1) grid[y * w + x] = d[y]
  }
  for (let y = 0; y < h; y += 1) {
    const row = y * w
    for (let x = 0; x < w; x += 1) f[x] = grid[row + x]
    pass(w)
    for (let x = 0; x < w; x += 1) grid[row + x] = d[x]
  }
  const hex = /^#?([0-9a-f]{6})$/i.exec(color)?.[1] ?? 'ffffff'
  const r = parseInt(hex.slice(0, 2), 16)
  const g = parseInt(hex.slice(2, 4), 16)
  const b = parseInt(hex.slice(4, 6), 16)
  for (let i = 0; i < w * h; i += 1) {
    const cover = Math.min(1, Math.max(0, t + 0.5 - Math.sqrt(grid[i])))
    const j = i * 4
    data[j] = r
    data[j + 1] = g
    data[j + 2] = b
    data[j + 3] = Math.round(cover * 255)
  }
}

/** A drawing turned about its middle, on a canvas as big as it then needs. */
export function turnedCanvas(drawn: HTMLCanvasElement, angle: number): HTMLCanvasElement {
  const turn = ((angle % 360) + 360) % 360
  if (turn === 0) return drawn
  const a = (turn * Math.PI) / 180
  const cos = Math.abs(Math.cos(a))
  const sin = Math.abs(Math.sin(a))
  const bw = cos * drawn.width + sin * drawn.height
  const bh = sin * drawn.width + cos * drawn.height
  const { canvas, pen } = canvasOf(bw, bh)
  pen.translate(canvas.width / 2, canvas.height / 2)
  pen.rotate(a)
  pen.drawImage(drawn, -drawn.width / 2, -drawn.height / 2)
  return canvas
}

/**
 * The lifted picture as it goes to a project's board or to Miro: outlined,
 * turned, see-through round it, as a PNG. Drawn at the picture's own
 * resolution, up to `longest` pixels on its longer side (2400, as the other cut
 * pictures: a PNG well under the 25 MB a staged picture may be).
 */
export async function renderFloat(source: ImageBitmap, spec: FloatSpec, longest = 2400): Promise<Blob> {
  const own = source.width / Math.max(1, spec.w)
  const reach = Math.max(spec.w, spec.h) + 2 * spec.outline
  const perUnit = Math.min(own, longest / Math.max(1, reach * Math.SQRT2))
  // A translucent ground is made solid first; a cut-out is outlined along its shape.
  const kind = alphaKind(source)
  const art = kind === 'semi' ? flattenAlpha(source) : source
  const drawn = turnedCanvas(drawOutlined(art, spec, perUnit, kind === 'cutout'), spec.angle)
  return new Promise<Blob>((resolve, reject) =>
    drawn.toBlob((out) => (out ? resolve(out) : reject(new Error('Could not draw the lifted picture'))), 'image/png'),
  )
}

/**
 * The lifted picture for Miro, outlined but not turned (Huanrui, 2026-10-23):
 * Miro turns the item itself, so its corners need not be see-through, and a
 * picture without see-through parts goes as a JPEG at `quality` -- the same
 * as every other picture on the sheet -- instead of a large, slow PNG (a
 * full-size photo took 7 seconds to encode as one). A cut-out stays a
 * see-through PNG. Up to `longest` pixels on its longer side, the outline
 * included; turned on Miro, its slanted edges are drawn by Miro, sharp.
 */
export async function renderFloatFlat(source: ImageBitmap, spec: FloatSpec, quality: number, longest = 2400): Promise<Blob> {
  const own = source.width / Math.max(1, spec.w)
  const reach = Math.max(spec.w, spec.h) + 2 * spec.outline
  const perUnit = Math.min(own, longest / Math.max(1, reach))
  const kind = alphaKind(source)
  const art = kind === 'semi' ? flattenAlpha(source) : source
  const drawn = drawOutlined(art, spec, perUnit, kind === 'cutout')
  if (kind !== 'cutout') {
    // White under it, so nothing the quick look missed turns black in a JPEG.
    const pen = drawn.getContext('2d')
    if (!pen) throw new Error('Canvas is unavailable')
    pen.globalCompositeOperation = 'destination-over'
    pen.fillStyle = '#ffffff'
    pen.fillRect(0, 0, drawn.width, drawn.height)
    pen.globalCompositeOperation = 'source-over'
  }
  const type = kind === 'cutout' ? 'image/png' : 'image/jpeg'
  return new Promise<Blob>((resolve, reject) =>
    drawn.toBlob((out) => (out ? resolve(out) : reject(new Error('Could not draw the lifted picture'))), type, quality),
  )
}
